'use client'

import { usePathname, useRouter } from 'next/navigation'
import { Suspense, useState } from 'react'
import { MdClose, MdLogin, MdLogout } from 'react-icons/md'

import ErrorBoundary from '../shared/error-boundary'
import PageLoading from '../shared/page-loading'
import ToastFallback from '../shared/toast-fallback'

import { ExitSurveyAlert, InstallBanner } from '@/components'
import redirectToLogout from '@/libs/actions/redirect-to-logout'
import { useGetClientConfigQuery } from '@/libs/apis/queries/use-get-client-config-query'
import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import { usePreventExitSurvey } from '@/libs/hooks/use-prevent-exit-survey'
import { useSidebarMenu } from '@/libs/hooks/use-sidebar-menu'
import { toggleSidebar } from '@/libs/store/slice/ui-slice'
import { TSidebarMenu } from '@/libs/types/global.types'

type TSidebar = {
  readonly isGuest?: boolean
  readonly isAuthPartner?: boolean
}

export default function Sidebar({ isGuest, isAuthPartner }: TSidebar) {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const currentPathname = usePathname()
  const { username, partnerId } = useAppSelector((state) => state.user)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedMenu, setSelectedMenu] = useState<TSidebarMenu | null>(null)
  const { sidebarMenu } = useSidebarMenu()

  const {
    showSidebar: isOpen,
    accountDeletion,
    suspend,
    accountDeletedPir,
  } = useAppSelector((state) => state.ui)

  const { checkPreventStatus, showPreventAlert, setShowPreventAlert, dismiss } =
    usePreventExitSurvey()

  const onClose = () => {
    dispatch(toggleSidebar(false))
  }

  const onSelectPathname = (pathname: string, openInNewTab: boolean) => {
    onClose()

    if (pathname === '/home' && suspend) {
      return router.push(username ? '/suspend' : '/auth/suspend')
    }

    if (openInNewTab) {
      return window.open(pathname, '_blank')
    }

    return router.push(pathname)
  }

  const onLogout = async () => {
    setIsLoading(true)
    onClose()

    await redirectToLogout()
  }

  const { data } = useGetClientConfigQuery({
    clientId: partnerId,
    // queryOption: { enabled: false },
  })

  return (
    <>
      <ExitSurveyAlert
        isOpen={showPreventAlert}
        onClose={() => setShowPreventAlert(false)}
        onSubmit={() => {
          dismiss()
          setShowPreventAlert(false)
          if (selectedMenu?.pathname) {
            return onSelectPathname(selectedMenu?.pathname, selectedMenu?.openInNewTab)
          }
        }}
      />
      <PageLoading show={isLoading} />
      <aside
        className={`fixed right-0 top-0 z-50 h-full w-full flex-row bg-pop-gray-1/50 ${
          isOpen ? 'flex' : 'invisible'
        }`}
      >
        <div className={`flex-grow ${isOpen ? 'flex' : 'invisible'}`} />
        <div
          className={`relative w-[360px] overflow-auto bg-pop-gray-5 pb-36 transition-transform duration-300 ease-in-out ${
            isOpen ? 'transform-none' : 'translate-x-full transform'
          }`}
        >
          <div className='flex flex-row items-center justify-between bg-pop-darker px-5 py-4'>
            <span className='text-xl font-bold'>Menu</span>
            <MdClose
              className='h-7 w-7 cursor-pointer text-pop-grey-900'
              onClick={onClose}
              data-testid='close-icon'
            />
          </div>
          <ul className='flex flex-col'>
            {sidebarMenu.map((res) => {
              if (
                (!accountDeletedPir &&
                  ((isAuthPartner && !res.isAuthPartner) ||
                    (isGuest && !res?.showForGuest) ||
                    (res?.title === 'Profile' && data?.result?.useOwnLoyaltyPlatform) ||
                    (res?.title === 'Notification Settings' &&
                      isGuest &&
                      !('Notification' in window)) ||
                    (res?.hideOnDeletionAccount && accountDeletion) ||
                    (res?.hideOnSuspend && suspend))) ||
                (res?.hideOnDeletedAccountPir && accountDeletedPir)
              ) {
                return null
              }

              const isSurveyListDisabled =
                res?.title === 'Survey List' &&
                currentPathname === '/auth/partner' &&
                accountDeletedPir

              return (
                <li key={res?.pathname}>
                  <button
                    type='button'
                    disabled={isSurveyListDisabled}
                    className={`w-full cursor-pointer border border-b-pop-gray-5 p-5 text-left text-base ${
                      currentPathname === res?.pathname || isSurveyListDisabled
                        ? 'bg-pop-blue-50 font-semibold text-pop-business-blue'
                        : 'bg-white font-normal'
                    }`}
                    onClick={() => {
                      if (checkPreventStatus()) {
                        onClose()
                        return setSelectedMenu(res)
                      }

                      if (res?.onClick) {
                        onClose()
                        return res?.onClick()
                      } else if (res?.pathname) {
                        return onSelectPathname(res?.pathname, res?.openInNewTab)
                      }
                    }}
                  >
                    {res?.title}
                  </button>
                </li>
              )
            })}
            {suspend || isAuthPartner ? null : (
              <li key='install'>
                <Suspense>
                  <ErrorBoundary fallback={<ToastFallback />}>
                    <InstallBanner disableClose keepShow />
                  </ErrorBoundary>
                </Suspense>
              </li>
            )}
            {!isGuest && username && (
              <li key='logout'>
                <button
                  className={`flex w-full cursor-pointer flex-row items-center gap-2 border border-b-pop-gray-5 bg-white p-5 text-base font-normal text-pop-red-500`}
                  onClick={onLogout}
                  type='button'
                >
                  Log Out
                  <MdLogout className='h-6 w-6 cursor-pointer' />
                </button>
              </li>
            )}
            {!username && !accountDeletedPir && (
              <li key='back_to_login'>
                <button
                  className={`flex w-full cursor-pointer flex-row items-center gap-2 border border-b-pop-gray-5 bg-white p-5 text-base font-normal text-pop-blue-500`}
                  onClick={() => router.replace('/auth/login')}
                  type='button'
                >
                  Back to Login
                  <MdLogin className='h-6 w-6 cursor-pointer' />
                </button>
              </li>
            )}
          </ul>
        </div>
      </aside>
    </>
  )
}
